import { SharePointListClient, type ListItem } from '../SharePointListClient';

/**
 * 99.ABM_Proveedores — vendor master. A subset of fields round-trip between
 * PowerApps and Graph as strings (e.g. Identificacion_P is a CUIT/CUIL).
 * TipoRetenciones_P + Concepto_P are populated together when RecibeRetenciones_P='SI'.
 */
export type ProveedoresFields = {
  Title?: string | null;
  Nombre_P?: string | null;
  Tipo_P?: 'CUIT' | 'CUIL' | string | null;
  Identificacion_P?: string | null;
  CondicionIVA_P?: string | null;
  CondicionPago_P?: string | null;
  Email_P?: string | null;
  Telefono_P?: string | null;
  Pais_P?: string | null;
  Provincia_P?: string | null;
  Localidad_P?: string | null;
  DireccionCompleta_P?: string | null;
  TipoRetenciones_P?: string | null;
  /** ConcatCodigoRegimenConcepto_R from 99.ABM_Retenciones. */
  Concepto_P?: string | null;
  RecibeRetenciones_P?: 'SI' | 'NO' | string | null;
  Status_P?: 'Activo' | 'Inactivo' | string | null;
};

export type ProveedorItem = ListItem<ProveedoresFields>;

export const proveedoresClient = new SharePointListClient<ProveedoresFields>('ABM_Proveedores');

export async function listAllProveedores(): Promise<ProveedorItem[]> {
  return proveedoresClient.listItems({ top: 5000 });
}

export async function listActiveProveedores(): Promise<ProveedorItem[]> {
  return proveedoresClient.listItems({
    filter: "fields/Status_P eq 'Activo'",
    orderBy: 'fields/Nombre_P asc',
  });
}

export type ProveedorPayload = {
  nombre: string;
  // 'RFC' added for MX — Mexican proveedores carry an RFC instead of
  // CUIT/CUIL. The corresponding SP column `Tipo_P` is free-text on
  // the SharePoint side, so storing 'RFC' is safe.
  tipo: 'CUIT' | 'CUIL' | 'RFC';
  identificacion?: string | null;
  condicionIVA?: string | null;
  condicionPago?: string | null;
  email?: string | null;
  telefono?: string | null;
  pais?: string | null;
  provincia?: string | null;
  localidad?: string | null;
  direccionCompleta?: string | null;
  recibeRetenciones: boolean;
  tipoRetenciones?: string | null;
  /** `ConcatCodigoRegimenConcepto_R` of the selected Retención. */
  conceptoRetencion?: string | null;
};

function fieldsFromPayload(p: ProveedorPayload): Partial<ProveedoresFields> {
  const trim = (v?: string | null) => (v && v.trim() ? v.trim() : null);
  return {
    Nombre_P: p.nombre.trim(),
    Tipo_P: p.tipo,
    Identificacion_P: trim(p.identificacion),
    CondicionIVA_P: trim(p.condicionIVA),
    CondicionPago_P: trim(p.condicionPago),
    Email_P: trim(p.email),
    Telefono_P: trim(p.telefono),
    Pais_P: trim(p.pais),
    Provincia_P: trim(p.provincia),
    Localidad_P: trim(p.localidad),
    DireccionCompleta_P: trim(p.direccionCompleta),
    RecibeRetenciones_P: p.recibeRetenciones ? 'SI' : 'NO',
    TipoRetenciones_P: p.recibeRetenciones ? trim(p.tipoRetenciones) : null,
    Concepto_P: p.recibeRetenciones ? trim(p.conceptoRetencion) : null,
  };
}

export async function createProveedor(payload: ProveedorPayload): Promise<ProveedorItem> {
  return proveedoresClient.createItem({
    Title: '[sumar]',
    Status_P: 'Activo',
    ...fieldsFromPayload(payload),
  });
}

export async function updateProveedor(
  id: string,
  payload: ProveedorPayload,
): Promise<ProveedorItem> {
  return proveedoresClient.updateItem(id, fieldsFromPayload(payload));
}

export async function softDeleteProveedor(id: string): Promise<ProveedorItem> {
  return proveedoresClient.updateItem(id, { Status_P: 'Inactivo' });
}
